// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';

// Create Context
export const AuthContext = createContext();

// Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // User object
  const [loading, setLoading] = useState(true); // Loading state

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // On component mount, check for existing token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch user data
      fetch(`${API_BASE_URL}/auth/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            setUser(data);
          } else {
            // Invalid token
            localStorage.removeItem('token');
            setUser(null);
          }
        })
        .catch(() => {
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // Login function
  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('token', data.token);
        // Fetch user data
        const userRes = await fetch(`${API_BASE_URL}/auth/user`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${data.token}` },
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
          return { success: true };
        }
      }
      return { success: false, message: data.error || 'Login failed.' };
    } catch (error) {
      return { success: false, message: 'An error occurred during login.' };
    }
  };

  // Signup function
  const signup = async (email, password, agreeToTOS, agreeToPrivacy) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, agreeToTOS, agreeToPrivacy }),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('token', data.token);
        // Fetch user data
        const userRes = await fetch(`${API_BASE_URL}/auth/user`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${data.token}` },
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
          return { success: true };
        }
      }
      return { success: false, message: data.error || 'Signup failed.' };
    } catch (error) {
      return { success: false, message: 'An error occurred during signup.' };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

// Request Password Reset
const requestResetPassword = async (email) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/request-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message || 'Reset link sent.' };
      }
      return { success: false, message: data.error || 'Request failed.' };
    } catch (error) {
      return { success: false, message: 'An error occurred.' };
    }
  };

  // Reset Password
  const resetPassword = async (token, newPassword) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        return { success: true, message: data.message || 'Password reset successful.' };
      }
      return { success: false, message: data.error || 'Reset failed.' };
    } catch (error) {
      return { success: false, message: 'An error occurred.' };
    }
  };

  // Get Download Status
  const getDownloadStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { 
        canDownload: false,
        remainingDownloads: 0,
        weeklyLimit: 0,
        unlimited: false
      };
    }

    try {
      console.log('Fetching download status from API...');
      const res = await fetch(`${API_BASE_URL}/auth/download-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Raw API response for download status:', data);
        console.log('API returned unlimited property:', data.unlimited);
        console.log('Worker version:', data.workerVersion || 'not specified');
        
        return { 
          canDownload: data.canDownload,
          remainingDownloads: data.remainingDownloads,
          weeklyLimit: data.weeklyLimit,
          message: data.message,
          unlimited: data.unlimited || false,
          workerVersion: data.workerVersion
        };
      } else {
        return { 
          canDownload: false,
          remainingDownloads: 0,
          weeklyLimit: 0,
          message: 'Failed to fetch download status',
          unlimited: false
        };
      }
    } catch (error) {
      console.error('Error fetching download status:', error);
      return { 
        canDownload: false,
        remainingDownloads: 0,
        weeklyLimit: 0,
        message: 'Error checking download status',
        unlimited: false
      };
    }
  };

  // Get User Stats
  const getUserStats = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/user-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        console.error('Failed to fetch user stats');
        return null;
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return null;
    }
  };

  // Get User Download History
  const getUserDownloads = async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { downloads: [], pagination: { total: 0, page: 1, limit: 10, pages: 0 } };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/auth/download-history?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        console.error('Failed to fetch download history');
        return { downloads: [], pagination: { total: 0, page: 1, limit: 10, pages: 0 } };
      }
    } catch (error) {
      console.error('Error fetching download history:', error);
      return { downloads: [], pagination: { total: 0, page: 1, limit: 10, pages: 0 } };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      signup, 
      logout, 
      requestResetPassword, 
      resetPassword, 
      getDownloadStatus,
      getUserStats,
      getUserDownloads
    }}>
      {children}
    </AuthContext.Provider>
  );
};